import {
  WeatherWarningViewModel,
  PestRiskViewModel,
  DiseaseRiskViewModel,
  UserEnterpriseInfoViewModel,
  WarningsAndAlertsViewModelResponseResult,
  HomePageProxy,
  DiseaseRiskDateModel,
} from '@/app/proxy/proxies';
import { DkTabProps } from '@/app/shared/components/dk-tabs/dk-tabs.component';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs';
import { WidgetInfoBoxComponent } from './widget-info-box/widget-info-box.component';
import { WidgetNoDeviceComponent } from './widget-no-device/widget-no-device.component';
import { SafeImageDirective } from '@/app/shared/directives/safe-image.directive';
import { WidgetWeekDaysComponent } from './widget-week-days/widget-week-days.component';
import { WidgetPestDetailsComponent } from './widget-pest-details/widget-pest-details.component';
import { DateTime } from 'luxon';
import { PlantationService } from '@/app/core/services/plantation.service';
import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';

export interface WeatherWarningCard {
  alertName: string;
  alertDescription: string;
  alertLevel: string;
  alertLevelColor: string;
  icon: string;
  landId: string;
  landName: string;
  plantationId: string;
  plantationName: string;
  cropColor: string;
  plantationImage: string;
  totalAreaDisplay: string;
}

@Component({
  selector: 'app-home-warnings-and-alerts',
  imports: [SharedModule, WidgetInfoBoxComponent, WidgetNoDeviceComponent, SafeImageDirective, WidgetWeekDaysComponent, WidgetPestDetailsComponent],
  templateUrl: './home-warnings-and-alerts.component.html',
  styleUrl: './home-warnings-and-alerts.component.less',
  providers: [HomePageProxy],
})
export class HomeWarningsAndAlertsComponent implements OnChanges {
  @Input() selectedEnterprise: UserEnterpriseInfoViewModel;

  @Output() readonly requestCallbackEvent = new EventEmitter<void>();
  @Output() readonly analyticsWarningsLoaded = new EventEmitter<{
    weatherWarnings: number;
    pestsWarnings: number;
    diseaseRiskWarnings: number;
    hasPestDevice: boolean;
    fetchingError: boolean;
  }>();
  alertTabs: DkTabProps[] = [
    {
      label: 'doktar_app.common.all',
      value: 'All',
    },
    {
      label: 'doktar_app.common.weather',
      value: 'Weather',
    },
    {
      label: 'doktar_app.common.pests',
      value: 'Pests',
    },
    {
      label: 'doktar_app.common.disease_risk',
      value: 'Disease Risk',
    },
  ];
  selectedAlertTab: DkTabProps = this.alertTabs[0];

  alertFeedsLoading: boolean = true;
  weatherFeeds: WeatherWarningViewModel[] = [];
  weatherCards: WeatherWarningCard[] = [];
  pestsFeeds: PestRiskViewModel[] = [];
  diseaseRiskFeeds: DiseaseRiskViewModel[] = [];
  fetchingError= false;
  hasPestDevice = false;
  openDiseasePanel: string | number = '0';
  readonly weatherHorizonHours = 48;
  private diseaseAccordionInitialized = false;

  constructor(
    private _homeProxy: HomePageProxy,
    private _plantationService: PlantationService,
    private mixpanelTracker: MixpanelTrackerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedEnterprise']) {
      this.resetAlertFeeds();
      this.alertFeedsLoading = true;
      this.fetchingError = false;
      this.hasPestDevice = false;

      if (this.selectedEnterprise) {
        this.hasPestDevice = this._plantationService.checkThisEnterpriseHasDevice(this.selectedEnterprise.enterpriseId, 'pst')
        this.getAlertFeeds();
      }
    }
  }

  get dataSourceKey(): string {
    switch (this.selectedAlertTab?.value) {
      case 'All':
        return 'doktar_app.home.warnings_data_source';
      case 'Weather':
      case 'Disease Risk':
        return 'doktar_app.home.warnings.data_source_visualcrossing';
      case 'Pests':
        return 'doktar_app.home.warnings.data_source_pesttrap';
      default:
        return '';
    }
  }

  get hasWeatherHorizon(): boolean {
    return this.selectedAlertTab?.value === 'Weather';
  }

  get hasAnyAlertFeeds(): boolean {
    return this.weatherCards.length > 0 || this.pestsFeeds.length > 0 || this.diseaseRiskFeeds.length > 0;
  }

  changeAlertTab(tab: DkTabProps) {
    if (this.selectedAlertTab?.value !== tab.value) {
      this.mixpanelTracker.trackHomeWarningsFilterTabSelected({
        'filter tab': tab.value,
      });
    }
    this.selectedAlertTab = tab;
  }

  onDiseaseAccordionChange(value: string | number | (string | number)[]): void {
    // First valueChange comes from initial binding, ignore it
    if (!this.diseaseAccordionInitialized) {
      this.diseaseAccordionInitialized = true;
      return;
    }

    let index: number | null = null;

    if (Array.isArray(value)) {
      if (!value.length) return;
      index = Number(value[value.length - 1]);
    } else if (value !== null && value !== undefined) {
      index = Number(value);
    }

    if (index === null || Number.isNaN(index)) {
      return;
    }

    const disease = this.diseaseRiskFeeds[index];
    if (!disease) return;

    this.mixpanelTracker.trackHomeWarningExpanded({
      'warning subject': 'Disease Risk',
      'filter tab': this.selectedAlertTab?.value ?? '',
    });
  }

  onDiseaseAccordionOpen(event: { index?: number } | null | undefined): void {
    // PrimeNG Accordion `onOpen` payload typically contains the opened panel index.
    const index = Number(event?.index);
    if (Number.isNaN(index)) return;

    const disease = this.diseaseRiskFeeds[index];
    if (!disease) return;

    this.mixpanelTracker.trackHomeWarningExpanded({
      'warning subject': 'Disease Risk',
      'filter tab': this.selectedAlertTab?.value ?? '',
    });
  }

  onPestAccordionOpen(event: { index?: number } | null | undefined): void {
    const index = Number(event?.index);
    if (Number.isNaN(index)) return;

    const pest = this.pestsFeeds[index];
    if (!pest) return;

    this.mixpanelTracker.trackHomeWarningExpanded({
      'warning subject': 'Pests',
      'filter tab': this.selectedAlertTab?.value ?? '',
    });
  }

  getAlertFeeds(): void {
    this.resetAlertFeeds();
    this.fetchingError = false;
    this.alertFeedsLoading = true;
    this._homeProxy
      .homePage_GetWarningsAndAlertByEnterprise(this.selectedEnterprise?.enterpriseId)
      .pipe(
        finalize(() => {
          this.alertFeedsLoading = false;
        })
      )
      .subscribe({
        next: (res: WarningsAndAlertsViewModelResponseResult) => {
          if (res.isSuccess) {
            this.weatherFeeds = res.result?.weatherWarning || [];
            this.weatherCards = this.flattenWeatherCards(this.weatherFeeds);
            this.pestsFeeds = res.result?.pestRisk || [];
            this.diseaseRiskFeeds =
              res.result?.diseaseRisk?.filter((d) => (d.diseaseRiskDates ?? []).some((dr) => dr.riskLevel > 0)) ?? [];
            this.analyticsWarningsLoaded.emit({
              weatherWarnings: this.weatherFeeds.length,
              pestsWarnings: this.pestsFeeds.length,
              diseaseRiskWarnings: this.diseaseRiskFeeds.length,
              hasPestDevice: this.hasPestDevice,
              fetchingError: false,
            });
          } else {
            this.fetchingError = true;
            this.emitFailedAnalytics();
          }
        },
        error: () => {
          this.fetchingError = true;
          this.emitFailedAnalytics();
        },
      });
  }

  flattenWeatherCards(alerts: WeatherWarningViewModel[] | undefined): WeatherWarningCard[] {
    return (alerts ?? []).flatMap((alert) =>
      (alert.lands ?? []).flatMap((land) =>
        (land.plantations ?? []).map((plantation) => ({
          alertName: alert.alertName ?? '',
          alertDescription: alert.alertDescription ?? '',
          alertLevel: alert.alertLevel ?? '',
          alertLevelColor: alert.alertLevelColor ?? '',
          icon: alert.icon ?? '',
          landId: land.landId ?? '',
          landName: land.landName ?? '',
          plantationId: plantation.plantationId ?? '',
          plantationName: plantation.plantationName ?? '',
          cropColor: plantation.cropColor ?? '',
          plantationImage: plantation.plantationImage ?? '',
          totalAreaDisplay: plantation.totalAreaDisplay ?? '',
        }))
      )
    );
  }

  getWeatherRiskLevel(alertLevel: string | undefined): number {
    const level = alertLevel?.trim().toLowerCase();
    if (!level) {
      return 0;
    }
    if (level === 'high' || level === '3') {
      return 3;
    }
    return 1;
  }

  expectedLastDayText(diseaseRiskDates: DiseaseRiskDateModel[]): number {
    const dates = diseaseRiskDates.map((d) => d.diseaseDate);
    if (!dates || dates.length === 0) return 0;

    // Tarihleri normalize edip (sadece gün olarak) sıralıyoruz
    const sortedDates = [...new Set(dates.map((d) => d.startOf('day').toISO()))]
      .map((d) => DateTime.fromISO(d))
      .sort((a, b) => a.toMillis() - b.toMillis());

    let maxStreak = 1;
    let currentStreak = 1;

    for (let i = 1; i < sortedDates.length; i++) {
      const diffDays = sortedDates[i].diff(sortedDates[i - 1], 'days').days;

      if (diffDays === 1) {
        currentStreak++;
        maxStreak = Math.max(maxStreak, currentStreak);
      } else {
        currentStreak = 1;
      }
    }

    return maxStreak;
  }

  hasMultipleRiskLevels(diseaseRiskDates: DiseaseRiskDateModel[]): boolean {
    if (!diseaseRiskDates?.length) return false;
    // Mirrors the grouping in WidgetWeekDaysComponent so the badge shown in the
    // header and the badges shown per day row never both appear.
    const levels = new Set(
      diseaseRiskDates.filter((d) => !!d?.diseaseDate).map((d) => d.riskLevel ?? 0)
    );
    return levels.size > 1;
  }

  isDiseasePanelExpanded(index: number): boolean {
    return String(this.openDiseasePanel) === String(index);
  }

  showDiseaseHeaderBadge(index: number, diseaseRiskDates: DiseaseRiskDateModel[]): boolean {
    return !(this.isDiseasePanelExpanded(index) && this.hasMultipleRiskLevels(diseaseRiskDates));
  }

  getDiseaseRiskLevel(diseaseRiskDates: DiseaseRiskDateModel[]): number {
    let hasAnyHighRisk = false;
    diseaseRiskDates.forEach((d) => {
      if (d.riskLevel > 1) {
        hasAnyHighRisk = true;
      }
    });
    if (hasAnyHighRisk) {
      return 3;
    }
    let hasAnyLowRisk = false;
    diseaseRiskDates.forEach((d) => {
      if (d.riskLevel < 3 && d.riskLevel > 0) {
        hasAnyLowRisk = true;
      }
    });
    if (hasAnyLowRisk) {
      return 1;
    }
    return 0;
  }

  private resetAlertFeeds(): void {
    this.weatherFeeds = [];
    this.weatherCards = [];
    this.pestsFeeds = [];
    this.diseaseRiskFeeds = [];
  }

  private emitFailedAnalytics(): void {
    this.analyticsWarningsLoaded.emit({
      weatherWarnings: 0,
      pestsWarnings: 0,
      diseaseRiskWarnings: 0,
      hasPestDevice: this.hasPestDevice,
      fetchingError: true,
    });
  }
}
